import { getStorageSession, registerStorageResponse } from './storageUrls.js';
import { create, toBinary, fromBinary } from '@bufbuild/protobuf';
import { timestampFromDate, timestampDate } from '@bufbuild/protobuf/wkt';
import {
  WatchAppEventsRequestSchema,
  WatchAppEventsResponseSchema,
} from '../gen/realtime/watch_app_events_pb';
import {
  shouldDispatch,
  locationCreatedToEvent,
  locationUpdatedToEvent,
  locationDeletedToEvent,
  projectImageCreatedToEvent,
  imageRotatedToEvent,
  inviteCreatedToEvent,
  inviteDeletedToEvent,
  memberJoinedToEvent,
  memberRemovedToEvent,
  memberRoleChangedToEvent,
  ownershipTransferredToEvent,
  imageCategorizedToEvent,
  imagesBulkCategorizedToEvent,
  projectImageMovedToEvent,
  projectImagesBulkMovedToEvent,
  projectImageDeletedToEvent,
  uncategorizedImagesDeletedToEvent,
  generationStartedToEvent,
  generationCompletedToEvent,
  generationUpdatedToEvent,
  generationFailedToEvent,
  generationLikesUpdatedToEvent,
  generationDeletedToEvent,
  generationCropUpdatedToEvent,
  imageMaskRegionsUpdatedToEvent,
  generationMaskRegionsUpdatedToEvent,
  displaySelectionChangedToEvent,
  imagesReorderedToEvent,
  creditsUpdatedToEvent,
  chatMessageCreatedToEvent,
  chatMessageDeletedToEvent,
  isBoardGeneration,
  boardGenerationStartedToEvent,
  boardGenerationCompletedToEvent,
  boardGenerationFailedToEvent,
  boardGenerationDeletedToEvent,
  projectSettingsUpdatedToEvent,
  projectDeletedToEvent,
  accountDeletedToEvent,
  shotlistShotsCreatedToEvent,
  shotlistShotUpdatedToEvent,
  shotlistShotDeletedToEvent,
  shotlistShotHiddenToEvent,
  shotlistCastCreatedToEvent,
  shotlistCastUpdatedToEvent,
  shotlistCastDeletedToEvent,
  shotlistPropCreatedToEvent,
  shotlistPropDeletedToEvent,
  shotlistPresetCreatedToEvent,
  shotlistConfigUpdatedToEvent,
  imageDescriptionUpdatedToEvent,
  imageFrameUpdatedToEvent,
} from './visionRealtimeMapping.js';
import { VisionPresenceKind } from '../gen/vision/projects/v1/vision_presence_pb';
import {
  trackVisionPresence as sendPresenceRpc,
  trackVisionPresenceUnload as sendPresenceRpcUnload,
} from './visionProjectsClient.js';
import { presenceFromProto, buildPresenceSnapshot } from './visionPresenceMapping.js';
import { getIdTokenForRequest } from './firebaseClient.js';
import { openGrpcCall } from './grpcWebSocket/call.js';
import { config } from '../config';

const APP_EVENTS_PATH = '/v1/grpc';
const APP_EVENTS_METHOD = '/protos.realtime.Realtime/WatchAppEvents';
const RECONNECT_DELAY_MS = 2_000;
const RECONNECT_VISIBILITY_GAP_MS = 5_000;

// Clean exits send an explicit LEAVE — in-app navigation (ProjectLayout unmount) and a
// tab/window close (the pagehide handler below). The heartbeat is therefore only a fallback
// for ungraceful drops (crash, network death, sleep), so it runs slowly; a peer that stops
// heartbeating is expired once its last announce predates the TTL. The wide heartbeat:TTL
// margin tolerates background-tab timer throttling without falsely evicting a present user.
const PRESENCE_HEARTBEAT_MS = 60_000;
const PRESENCE_TTL_MS = 180_000;
const PRESENCE_SWEEP_MS = 30_000;

let activeCall = null;
let openingAttempt = null;
let sessionEpoch = 0;
let sessionAccountId = null;
let subscriptionReady = false;
let currentProjectId = null;
// Reconnection is gated on this so the account-scoped socket stays open whenever authenticated,
// not only inside a project — account-level events like account deletion must reach a session
// sitting on the dashboard.
let connectionDesired = false;
let reconnectTimer = null;
let lastVisibleAt = Date.now();
// The instant to resume from on reconnect: the last event's `result.startAt` if the
// server stamped one, else null. A brief drop backfills from this instant instead of
// losing events during the gap.
let lastEventInstant = null;
// The moment joinProject was called. On the first connect (before any event has set
// lastEventInstant) the socket open is async, so watching from `now` at open-time would drop
// events published during the join→open gap (e.g. a collaborator opening an image just as a
// generation starts). Watching from the join instant backfills that gap; later events dedupe by id.
let joinInstant = null;

const eventHandlers = new Map();

// accountId -> { accountId, name, pfpUrl, imageId, generationId, gradingLock, cropLock, lastSeen }
// Folded into the consumer snapshot by buildPresenceSnapshot.
const presenceState = new Map();
const presenceListeners = new Set();
let selfPresence = null;
let firstAnnounceDone = false;
let heartbeatTimer = null;
let sweepTimer = null;

function buildWsUrl() {
  const wsBase = config.VISION_WS_BASE_URL.replace(/^http/i, 'ws');
  return `${wsBase}${APP_EVENTS_PATH}`;
}

/**
 * Dispatch an event to every handler registered via on() — each invoked in its own try/catch
 * so one throwing handler doesn't block the rest.
 */
function dispatch(event, payload) {
  const handlers = eventHandlers.get(event);
  if (!handlers) return;
  for (const handler of handlers) {
    try {
      handler(payload);
    } catch (err) {
      console.error(`[VisionRealtime] Error in handler for "${event}":`, err);
    }
  }
}

/**
 * Send the StartWatch first frame. `startAt` is REQUIRED by the server; it resumes from the
 * last seen instant on reconnect, else from the join instant so the join→open gap backfills,
 * else `now`.
 */
function sendStartWatch(call) {
  const startAt = timestampFromDate(lastEventInstant ?? joinInstant ?? new Date());
  const request = create(WatchAppEventsRequestSchema, {
    request: { case: 'startWatch', value: { params: { startAt } } },
  });
  return call.sendMessage(toBinary(WatchAppEventsRequestSchema, request));
}

function handleFrame(data, storageSession) {
  let response;
  try {
    response = fromBinary(WatchAppEventsResponseSchema, new Uint8Array(data));
    registerStorageResponse(response, storageSession);
  } catch (err) {
    console.error('[VisionRealtime] Failed to decode app-event frame:', err);
    return;
  }

  const result = response.result;
  if (!result) return;
  if (result.startAt) {
    lastEventInstant = timestampDate(result.startAt);
  }

  const payload = result.event?.payload;
  if (!payload) return;
  if (payload.case === 'status') {
    if (!subscriptionReady) {
      subscriptionReady = true;
      announcePresence(VisionPresenceKind.HELLO);
    }
    return;
  }

  let mapped = null;
  switch (payload.case) {
    case 'visionProjectLocationCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = locationCreatedToEvent(payload.value);
      break;
    }
    case 'visionProjectLocationUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = locationUpdatedToEvent(payload.value);
      break;
    }
    case 'visionProjectLocationDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = locationDeletedToEvent(payload.value);
      break;
    }
    case 'visionProjectImageCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectImageCreatedToEvent(payload.value);
      break;
    }
    case 'visionImageRotated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imageRotatedToEvent(payload.value);
      break;
    }
    case 'visionProjectImageCategorized': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imageCategorizedToEvent(payload.value);
      break;
    }
    case 'visionProjectImagesCategorized': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imagesBulkCategorizedToEvent(payload.value);
      break;
    }
    case 'visionProjectImageMoved': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectImageMovedToEvent(payload.value);
      break;
    }
    case 'visionProjectImagesBulkMoved': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectImagesBulkMovedToEvent(payload.value);
      break;
    }
    case 'visionProjectImageDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectImageDeletedToEvent(payload.value);
      break;
    }
    case 'visionProjectUncategorizedImagesDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = uncategorizedImagesDeletedToEvent(payload.value);
      break;
    }
    case 'visionGenerationStarted': {
      const projectId = payload.value.generation?.projectId;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      if (isBoardGeneration(payload.value.generation)) {
        mapped = boardGenerationStartedToEvent(payload.value);
      } else {
        mapped = generationStartedToEvent(payload.value);
      }
      break;
    }
    case 'visionGenerationCompleted': {
      const projectId = payload.value.generation?.projectId;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      if (isBoardGeneration(payload.value.generation)) {
        mapped = boardGenerationCompletedToEvent(payload.value);
      } else {
        mapped = generationCompletedToEvent(payload.value);
      }
      break;
    }
    case 'visionGenerationUpdated': {
      const projectId = payload.value.generation?.projectId;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = generationUpdatedToEvent(payload.value);
      break;
    }
    case 'visionGenerationLikesUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = generationLikesUpdatedToEvent(payload.value);
      break;
    }
    case 'visionGenerationDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      if (!payload.value.imageId) {
        mapped = boardGenerationDeletedToEvent(payload.value);
      } else {
        mapped = generationDeletedToEvent(payload.value);
      }
      break;
    }
    case 'visionGenerationCropUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = generationCropUpdatedToEvent(payload.value);
      break;
    }
    case 'visionImageMaskRegionsUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imageMaskRegionsUpdatedToEvent(payload.value);
      break;
    }
    case 'visionGenerationMaskRegionsUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = generationMaskRegionsUpdatedToEvent(payload.value);
      break;
    }
    case 'visionDisplaySelectionChanged': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = displaySelectionChangedToEvent(payload.value);
      break;
    }
    case 'visionImagesReordered': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imagesReorderedToEvent(payload.value);
      break;
    }
    case 'visionGenerationFailed': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      if (!payload.value.imageId) {
        mapped = boardGenerationFailedToEvent(payload.value);
      } else {
        mapped = generationFailedToEvent(payload.value);
      }
      break;
    }
    case 'visionProjectCreditsUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = creditsUpdatedToEvent(payload.value);
      break;
    }
    case 'visionChatMessageCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = chatMessageCreatedToEvent(payload.value);
      break;
    }
    case 'visionChatMessageDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = chatMessageDeletedToEvent(payload.value);
      break;
    }
    case 'visionInviteCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = inviteCreatedToEvent(payload.value);
      break;
    }
    case 'visionInviteDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = inviteDeletedToEvent(payload.value);
      break;
    }
    case 'visionMemberJoined': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = memberJoinedToEvent(payload.value);
      break;
    }
    case 'visionMemberRemoved': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = memberRemovedToEvent(payload.value);
      break;
    }
    case 'visionMemberRoleChanged': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = memberRoleChangedToEvent(payload.value);
      break;
    }
    case 'visionProjectOwnershipTransferred': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = ownershipTransferredToEvent(payload.value);
      break;
    }
    case 'visionProjectSettingsUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectSettingsUpdatedToEvent(payload.value);
      break;
    }
    case 'visionProjectDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = projectDeletedToEvent(payload.value);
      break;
    }
    case 'accountDeleted': {
      mapped = accountDeletedToEvent(payload.value);
      break;
    }
    case 'visionShotlistShotsCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistShotsCreatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistShotUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistShotUpdatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistShotDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistShotDeletedToEvent(payload.value);
      break;
    }
    case 'visionShotlistShotHidden': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistShotHiddenToEvent(payload.value);
      break;
    }
    case 'visionShotlistCastCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistCastCreatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistCastUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistCastUpdatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistCastDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistCastDeletedToEvent(payload.value);
      break;
    }
    case 'visionShotlistPropCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistPropCreatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistPropDeleted': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistPropDeletedToEvent(payload.value);
      break;
    }
    case 'visionShotlistPresetCreated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistPresetCreatedToEvent(payload.value);
      break;
    }
    case 'visionShotlistConfigUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = shotlistConfigUpdatedToEvent(payload.value);
      break;
    }
    case 'visionImageDescriptionUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imageDescriptionUpdatedToEvent(payload.value);
      break;
    }
    case 'visionImageFrameUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      mapped = imageFrameUpdatedToEvent(payload.value);
      break;
    }
    case 'visionPresenceUpdated': {
      const { projectId } = payload.value;
      if (!shouldDispatch(projectId, currentProjectId)) return;
      handlePresenceEvent(payload.value);
      return;
    }
    default:
      return;
  }

  if (!mapped) return;

  dispatch(mapped.event, mapped.payload);
}

function clearReconnectTimer() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
}

function isCurrentCall(call, epoch) {
  return connectionDesired && sessionEpoch === epoch && activeCall === call;
}

async function ensureSocket() {
  if (!connectionDesired || openingAttempt || activeCall) return;
  clearReconnectTimer();
  const attempt = { epoch: sessionEpoch };
  openingAttempt = attempt;
  try {
    const token = await getIdTokenForRequest();
    if (!connectionDesired || sessionEpoch !== attempt.epoch || openingAttempt !== attempt) return;
    if (!token) {
      scheduleReconnect();
      return;
    }

    const storageSession = getStorageSession();
    const call = openGrpcCall({
      url: buildWsUrl(),
      path: APP_EVENTS_METHOD,
      metadata: [['authorization', new TextEncoder().encode(`Bearer ${token}`)]],
      onMessage: (bytes) => {
        if (isCurrentCall(call, attempt.epoch)) handleFrame(bytes, storageSession);
      },
    });
    activeCall = call;
    subscriptionReady = false;
    void call.closed.then((outcome) => {
      if (!isCurrentCall(call, attempt.epoch)) return;
      activeCall = null;
      subscriptionReady = false;
      console.warn('[VisionRealtime] Subscription ended:', { source: outcome.source, code: outcome.code });
      scheduleReconnect();
    });

    await call.ready;
    if (!isCurrentCall(call, attempt.epoch)) return;
    await sendStartWatch(call);
    if (!isCurrentCall(call, attempt.epoch)) return;
    await call.closeSend();
  } catch {
    if (!connectionDesired || sessionEpoch !== attempt.epoch || openingAttempt !== attempt) return;
    if (activeCall) activeCall.cancel();
    else scheduleReconnect();
  } finally {
    if (openingAttempt === attempt) openingAttempt = null;
  }
}

function scheduleReconnect() {
  if (reconnectTimer || !connectionDesired) return;
  const epoch = sessionEpoch;
  const timer = setTimeout(() => {
    if (reconnectTimer !== timer) return;
    reconnectTimer = null;
    if (sessionEpoch === epoch && connectionDesired) ensureSocket();
  }, RECONNECT_DELAY_MS);
  reconnectTimer = timer;
}

function closeSocket() {
  clearReconnectTimer();
  const call = activeCall;
  activeCall = null;
  openingAttempt = null;
  subscriptionReady = false;
  call?.cancel();
}

export function joinProject(projectId, _userId) {
  // ProjectLayout always leaves first, but guard against a direct switch anyway.
  if (currentProjectId && currentProjectId !== projectId) {
    resetPresence();
  }

  currentProjectId = projectId;
  joinInstant = new Date();
  startPresenceTimers();
  ensureSocket();

  // The presence-tracking component can mount before the project bootstrap reaches this
  // point; its HELLO is dropped without a project id, which would leave this client
  // invisible to peers until the next heartbeat. Announce the join now instead.
  if (selfPresence) {
    announcePresence(VisionPresenceKind.HELLO);
  }
}

export function leaveProject() {
  announcePresence(VisionPresenceKind.LEAVE);
  resetPresence();
  dispatchPresence();
  currentProjectId = null;
}

export function on(event, handler) {
  if (!eventHandlers.has(event)) {
    eventHandlers.set(event, new Set());
  }
  eventHandlers.get(event).add(handler);
}

export function off(event, handler) {
  const handlers = eventHandlers.get(event);
  if (handlers) {
    handlers.delete(handler);
    if (handlers.size === 0) {
      eventHandlers.delete(event);
    }
  }
}

export function offAll(event) {
  eventHandlers.delete(event);
}

export function getCurrentProjectId() {
  return currentProjectId;
}

function dispatchPresence() {
  const snapshot = buildPresenceSnapshot(presenceState);
  for (const listener of presenceListeners) {
    try {
      listener(snapshot);
    } catch (err) {
      console.error('[VisionRealtime] Error in presence listener:', err);
    }
  }
}

/**
 * Publish this client's current presence to the project's members via the unary RPC. The
 * server resolves the sender's name + pfp_url and fans the event out to every member except
 * the sender (echo suppression at the source).
 */
function announcePresence(kind, viaUnload = false) {
  if (!selfPresence || !currentProjectId) return;
  if (!subscriptionReady && kind !== VisionPresenceKind.LEAVE) return;

  const send = viaUnload ? sendPresenceRpcUnload : sendPresenceRpc;

  send({
    projectId: currentProjectId,
    kind,
    imageId: selfPresence.imageId,
    generationId: selfPresence.generationId,
    gradingLockGenerationId: selfPresence.gradingLock,
    cropLockGenerationId: selfPresence.cropLock,
  }).catch((err) => {
    console.error('[VisionRealtime] Failed to send presence:', err);
  });
}

/**
 * Apply an inbound presence event: drop our own echo, evict on LEAVE, otherwise upsert the
 * peer. A HELLO is a freshly-joined peer asking everyone to announce, so we reply with our
 * own state — that is how a late joiner learns existing presence (and locks) on join.
 */
function handlePresenceEvent(value) {
  const entry = presenceFromProto(value);
  if (!entry.accountId) return;
  if (selfPresence && entry.accountId === selfPresence.accountId) return;

  if (entry.kind === VisionPresenceKind.LEAVE) {
    if (presenceState.delete(entry.accountId)) dispatchPresence();
    return;
  }

  presenceState.set(entry.accountId, { ...entry, lastSeen: Date.now() });
  dispatchPresence();

  if (entry.kind === VisionPresenceKind.HELLO) {
    announcePresence(VisionPresenceKind.UPDATE);
  }
}

function startPresenceTimers() {
  if (!heartbeatTimer) {
    heartbeatTimer = setInterval(() => {
      announcePresence(VisionPresenceKind.UPDATE);
    }, PRESENCE_HEARTBEAT_MS);
  }

  if (!sweepTimer) {
    sweepTimer = setInterval(() => {
      const cutoff = Date.now() - PRESENCE_TTL_MS;
      let changed = false;

      for (const [accountId, entry] of presenceState) {
        if (accountId === selfPresence?.accountId) continue;
        if (entry.lastSeen < cutoff) {
          presenceState.delete(accountId);
          changed = true;
        }
      }

      if (changed) dispatchPresence();
    }, PRESENCE_SWEEP_MS);
  }
}

function stopPresenceTimers() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }

  if (sweepTimer) {
    clearInterval(sweepTimer);
    sweepTimer = null;
  }
}

function resetPresence() {
  stopPresenceTimers();
  presenceState.clear();
  selfPresence = null;
  firstAnnounceDone = false;
}

// Evict a peer's presence by account id. A member who loses access can no longer be present, so
// membership removal drops their presence directly rather than waiting on their own LEAVE announce
// — which a leaver cannot send (the leave RPC removes their membership first) and a removed member
// never gets the chance to.
export function dropPresence(accountId) {
  if (presenceState.delete(accountId)) dispatchPresence();
}

export function onPresenceChange(listener) {
  presenceListeners.add(listener);

  try {
    listener(buildPresenceSnapshot(presenceState));
  } catch (err) {
    console.error('[VisionRealtime] Error in presence listener:', err);
  }

  return () => {
    presenceListeners.delete(listener);
  };
}

export function trackPresence(fields, userInfo) {
  if (!selfPresence) {
    if (!userInfo) return;

    selfPresence = {
      accountId: userInfo.userId,
      name: userInfo.displayName || '',
      pfpUrl: userInfo.pfpUrl || null,
      imageId: null,
      generationId: null,
      gradingLock: null,
      cropLock: null,
    };
  } else if (userInfo) {
    selfPresence.name = userInfo.displayName || '';
    selfPresence.pfpUrl = userInfo.pfpUrl || null;
  }

  if ('imageId' in fields) selfPresence.imageId = fields.imageId || null;
  if ('generationId' in fields) selfPresence.generationId = fields.generationId || null;
  if ('gradingLock' in fields) selfPresence.gradingLock = fields.gradingLock || null;
  if ('cropLock' in fields) selfPresence.cropLock = fields.cropLock || null;

  // Optimistic local self-render — the server echo-suppresses our own event.
  presenceState.set(selfPresence.accountId, { ...selfPresence, lastSeen: Date.now() });
  dispatchPresence();

  // The first announcement after a join is a HELLO (pull existing peers' state); later
  // changes are plain UPDATEs.
  const kind = firstAnnounceDone ? VisionPresenceKind.UPDATE : VisionPresenceKind.HELLO;
  firstAnnounceDone = true;
  announcePresence(kind);
}

export function untrackPresence() {
  announcePresence(VisionPresenceKind.LEAVE);

  if (selfPresence) presenceState.delete(selfPresence.accountId);
  selfPresence = null;
  dispatchPresence();
}

export function connectRealtime(accountId) {
  if (accountId !== undefined && connectionDesired && sessionAccountId !== accountId) disconnectRealtime();
  if (accountId !== undefined) sessionAccountId = accountId;
  connectionDesired = true;
  ensureSocket();
}

export function disconnectRealtime() {
  sessionEpoch++;
  sessionAccountId = null;
  connectionDesired = false;
  currentProjectId = null;
  lastEventInstant = null;
  joinInstant = null;
  resetPresence();
  presenceListeners.clear();
  closeSocket();
  eventHandlers.clear();
}

if (typeof window !== 'undefined') {
  // A tab/window close fires pagehide rather than a React unmount, so the in-app LEAVE never runs.
  window.addEventListener('pagehide', () => {
    announcePresence(VisionPresenceKind.LEAVE, true);
  });

  window.addEventListener('online', () => {
    scheduleReconnect();
  });
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      lastVisibleAt = Date.now();
      return;
    }
    if (!connectionDesired) return;
    const hiddenMs = Date.now() - lastVisibleAt;
    if (hiddenMs >= RECONNECT_VISIBILITY_GAP_MS) {
      ensureSocket();
    }
  });
}

export default {
  connectRealtime,
  joinProject,
  leaveProject,
  on,
  off,
  offAll,
  getCurrentProjectId,
  onPresenceChange,
  trackPresence,
  untrackPresence,
  disconnectRealtime,
};
